import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  ViewEncapsulation,
} from '@angular/core';
import { BrnDialogClose, BrnDialogRef } from '@spartan-ng/brain/dialog';
import type { ClassValue } from 'clsx';
import { mergeClasses } from 'src/app/@core/utils/merge-classes';
import { HlmDialogOverlayComponent } from './hlm-dialog-overlay.directive';

/**
 * Helm dialog content component (spartan layer).
 * Provides the visual panel container that appears inside the CDK overlay
 * when a dialog is open. Must be wrapped by `*hlmDialogPortal`.
 *
 * Handles the close button and overlay backdrop rendering.
 */
@Component({
  selector: 'hlm-dialog-content',
  standalone: true,
  imports: [HlmDialogOverlayComponent, BrnDialogClose],
  host: {
    '[class]': '_computedClass()',
  },
  template: `
    <hlm-dialog-overlay />
    <div [class]="_panelClass()" [attr.data-state]="_state()">
      <ng-content />
      @if (showCloseButton()) {
        <button
          brnDialogClose
          [class]="
            'absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground'
          "
          aria-label="Close dialog"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class HlmDialogContentComponent {
  private readonly _dialogRef = inject(BrnDialogRef, { optional: true });

  readonly userClass = input<ClassValue>('', { alias: 'class' });
  readonly showCloseButton = input<boolean>(true);

  protected readonly _computedClass = computed(() => 'contents');

  protected readonly _state = computed(() => this._dialogRef?.state() ?? 'closed');

  protected readonly _panelClass = computed(() =>
    mergeClasses(
      'fixed inset-0 m-auto z-50 grid w-full max-w-lg h-fit gap-4 bg-white p-6 shadow-lg',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
      'sm:rounded-lg',
      this.userClass(),
    ),
  );
}
